import { useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import {
  isExpiredDocument,
  PAGE_SIZE,
  ReviewDocument,
  ReviewFilters,
} from './reviewModel';

const advanced = (filters:ReviewFilters) => (filters.ai && filters.ai!=='all') || (filters.sort && filters.sort!=='oldest');
async function fetchControlled(filters:ReviewFilters, offset:number, limit:number) {
  const {data,error}=await (supabase as SupabaseClient).rpc('get_review_queue_controls',{
    p_status:filters.status,p_user:filters.user==='all'?null:filters.user,
    p_org:filters.org==='all'?null:filters.org,p_definition:filters.type==='all'?null:filters.type,
    p_ai:filters.ai||'all',p_sort:filters.sort||'oldest',p_offset:offset,p_limit:limit
  });
  if(error)throw error;
  if(!data || !Array.isArray(data.rows) || typeof data.total!=='number')throw new Error('Invalid queue response');
  return data as {rows:ReviewDocument[];total:number};
}
export async function fetchReviewBatch(filters: ReviewFilters, offset: number) {
  if(advanced(filters))return (await fetchControlled(filters,offset,PAGE_SIZE)).rows;
  const args = {
    filter_status: filters.status === 'overdue' ? 'all' : filters.status,
    offset_param: offset,
    limit_param: PAGE_SIZE,
    p_definition_id: filters.type === 'all' ? undefined : filters.type,
  };
  const result =
    filters.user !== 'all'
      ? await supabase.rpc('get_user_documents', {
          ...args,
          p_user_id: filters.user,
        })
      : filters.org !== 'all'
        ? await supabase.rpc('get_organization_documents', {
            ...args,
            org_id: filters.org,
          })
        : await supabase.rpc('get_all_documents', args);
  if (result.error) throw result.error;
  return result.data || [];
}
export async function fetchReviewCount(
  filters: ReviewFilters,
): Promise<number | null> {
  if(advanced(filters))return (await fetchControlled(filters,0,0)).total;
  // The existing count RPCs cannot count the expiry predicate. Never call that an expired total.
  if (filters.status === 'overdue') return null;
  const args = {
    filter_status: filters.status,
    p_definition_id: filters.type === 'all' ? undefined : filters.type,
  };
  const result =
    filters.user !== 'all'
      ? await supabase.rpc('count_user_documents', {
          ...args,
          p_user_id: filters.user,
        })
      : filters.org !== 'all'
        ? await supabase.rpc('count_organization_documents', {
            ...args,
            org_id: filters.org,
          })
        : await supabase.rpc('count_all_documents', args);
  if (result.error) throw result.error;
  const count = result.data?.[0]?.total_count;
  if (typeof count !== 'number' || !Number.isFinite(count) || count < 0)
    throw new Error('Conteo no disponible');
  return count;
}
interface Cache {
  key: string;
  rows: ReviewDocument[];
  batches: number;
  more: boolean;
}
export function useReviewQueue(
  filters: ReviewFilters,
  targetBatches: number,
  revision: number,
) {
  const [rows, setRows] = useState<ReviewDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [countError, setCountError] = useState(false);
  const [more, setMore] = useState(true);
  const [scannedBatches, setScannedBatches] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const cache = useRef<Cache | null>(null);
  const key = JSON.stringify({ ...filters, revision });
  useEffect(() => {
    let active = true;
    const snapshot: ReviewFilters = JSON.parse(key);
    let current =
      cache.current?.key === key && cache.current.batches <= targetBatches
        ? cache.current
        : { key, rows: [], batches: 0, more: true };
    const filtered = (data: ReviewDocument[]) =>
      snapshot.status === 'overdue'
        ? data.filter((doc) => isExpiredDocument(doc))
        : data;
    setRows(filtered(current.rows));
    setScannedBatches(current.batches);
    setMore(current.more);
    setLoading(true);
    setError(null);
    setTotal(null);
    setCountError(false);
    setUpdatedAt(null);
    fetchReviewCount(snapshot)
      .then((value) => {
        if (active) setTotal(value);
      })
      .catch(() => {
        if (active) setCountError(true);
      });
    (async () => {
      try {
        while (current.more && current.batches < targetBatches) {
          const batch = await fetchReviewBatch(
            snapshot,
            current.batches * PAGE_SIZE,
          );
          if (!active) return;
          const unique = new Map(
            current.rows.map((doc) => [doc.document_id, doc]),
          );
          batch.forEach((doc) => unique.set(doc.document_id, doc));
          // Advance on raw server batches, even when none match the expiry filter.
          current = {
            key,
            rows: Array.from(unique.values()),
            batches: current.batches + 1,
            more: batch.length === PAGE_SIZE,
          };
          cache.current = current;
          setRows(filtered(current.rows));
          setScannedBatches(current.batches);
          setMore(current.more);
        }
        if (active) setUpdatedAt(new Date());
      } catch {
        if (active)
          setError(
            'No se pudieron cargar todos los documentos. Reintenta para continuar.',
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [key, targetBatches]);
  return {
    rows,
    loading,
    error,
    total,
    countError,
    more,
    scannedBatches,
    updatedAt,
  };
}
